/*
 * Перенос наполнения одной ячейки на другие.
 *
 * Расчёт отделён от интерфейса: экран сначала показывает человеку масштаб
 * изменения и возможные пропуски, а затем применяет уже проверенный план.
 */
import type { ShelvingCellConfig, WardrobeModule } from "../types";
import {
  defaultShelvingCell,
  getShelvingDiagonalSections,
  getShelvingCellLayouts,
  getShelvingGridLayout,
  parseShelvingCellId,
  shelvingCellFits,
  shelvingCellId,
  shelvingExtraDividerPosition
} from "./shelving";

/**
 * Everything that describes how a cell is filled, minus where it sits and
 * minus the structure it shares with its neighbours. `leftDividerAngle` is
 * deliberately absent: that yaw belongs to a main grid upright shared with the
 * cell on the left, so stamping it across a row would rotate the carcass.
 * Diagonal spans are absent for the same reason — a section is a region, not a
 * cell property, and it has its own confirmed transaction.
 */
export type ShelvingCellFilling = Pick<
  ShelvingCellConfig,
  | "content"
  | "backPanel"
  | "extraShelf"
  | "extraShelfPosition"
  | "extraDivider"
  | "extraDividerMode"
  | "extraDividerPosition"
  | "extraDividerAngle"
  | "moreShelves"
  | "extraShelfGlass"
  | "moreShelvesGlass"
  | "moreDividers"
  | "lighting"
  | "lightTemperature"
  | "lightPosition"
  | "drawerCount"
  | "doorHinge"
  | "doorOpening"
  | "doorLift"
  | "facade"
  | "facadePattern"
  | "facadeMaterial"
  | "facadeDecor"
  | "facadeColor"
  | "facadeColorHex"
  | "facadeColorCode"
  | "facadePaintSides"
  | "handle"
>;

export type ShelvingBulkScope = "row" | "column" | "all";

export type ShelvingBulkSkipReason = "diagonal" | "size";

export interface ShelvingBulkPlan {
  /** Cells that will change, with the patch each one receives. */
  apply: Array<{ id: string; column: number; row: number; patch: ShelvingCellFilling }>;
  /** Cells left alone, with the reason. */
  skipped: Array<{ id: string; column: number; row: number; reason: ShelvingBulkSkipReason }>;
  /** Cells that take the filling without the local divider, which does not fit. */
  adjusted: string[];
  /** Cells that already carry a configuration and will lose it. */
  overwritten: string[];
}

const EMPTY_PLAN: ShelvingBulkPlan = {
  apply: [],
  skipped: [],
  adjusted: [],
  overwritten: []
};

export const shelvingCellFilling = (
  cell: ShelvingCellConfig
): ShelvingCellFilling => ({
  content: cell.content,
  backPanel: cell.backPanel,
  extraShelf: cell.extraShelf,
  extraShelfPosition: cell.extraShelfPosition,
  extraDivider: cell.extraDivider,
  extraDividerMode: cell.extraDividerMode,
  extraDividerPosition: cell.extraDividerPosition,
  extraDividerAngle: cell.extraDividerAngle,
  moreShelves: cell.moreShelves ? [...cell.moreShelves] : undefined,
  extraShelfGlass: cell.extraShelfGlass,
  moreShelvesGlass: cell.moreShelvesGlass ? [...cell.moreShelvesGlass] : undefined,
  moreDividers: cell.moreDividers ? [...cell.moreDividers] : undefined,
  lighting: cell.lighting,
  lightTemperature: cell.lightTemperature,
  lightPosition: cell.lightPosition,
  drawerCount: cell.drawerCount,
  doorHinge: cell.doorHinge,
  doorOpening: cell.doorOpening,
  doorLift: cell.doorLift,
  facade: cell.facade,
  facadePattern: cell.facadePattern,
  facadeMaterial: cell.facadeMaterial,
  facadeColor: cell.facadeColor,
  facadeColorHex: cell.facadeColorHex,
  facadeColorCode: cell.facadeColorCode,
  facadePaintSides: cell.facadePaintSides,
  handle: cell.handle
});

/** A cell counts as configured when it is no longer a plain open opening. */
export const shelvingCellIsConfigured = (cell: ShelvingCellConfig) =>
  cell.content !== "open" ||
  cell.backPanel ||
  cell.extraShelf ||
  Boolean(cell.extraDivider) ||
  Boolean(cell.moreShelves?.length || cell.moreDividers?.length) ||
  Boolean(cell.lighting && cell.lighting !== "none");

/**
 * Чьи настройки получит ячейка после объединения: выбранной, если она
 * настроена; иначе уже настроенной ячейки из области (например, витрины,
 * к которой присоединяют пустую ячейку). Только ячейки внутри области.
 */
export function shelvingMergeKeptCell(
  cells: readonly ShelvingCellConfig[],
  area: { left: number; right: number; bottom: number; top: number },
  keepCellId?: string,
  anchorId?: string
): ShelvingCellConfig | undefined {
  const inside = cells.filter((cell) => cell.column >= area.left && cell.column <= area.right &&
    cell.row >= area.bottom && cell.row <= area.top);
  const selected = keepCellId ? inside.find((cell) => cell.id === keepCellId) : undefined;
  if (selected && shelvingCellIsConfigured(selected)) return selected;
  return inside.find((cell) => cell.id === anchorId && shelvingCellIsConfigured(cell))
    ?? inside.find(shelvingCellIsConfigured)
    ?? selected
    ?? inside.find((cell) => cell.id === anchorId);
}

/**
 * Candidate cells for a scope, source excluded. Row and column are taken from
 * the source cell, so "the whole row" means the row the user is editing.
 */
export function shelvingBulkScopeIds(
  module: WardrobeModule,
  source: { column: number; row: number },
  scope: ShelvingBulkScope
): string[] {
  const grid = getShelvingGridLayout(module);
  const ids: string[] = [];
  for (let row = 0; row < grid.rows; row += 1) {
    for (let column = 0; column < grid.columns; column += 1) {
      if (column === source.column && row === source.row) continue;
      if (scope === "row" && row !== source.row) continue;
      if (scope === "column" && column !== source.column) continue;
      ids.push(shelvingCellId(module.id, column, row));
    }
  }
  return ids;
}

/**
 * Works out what stamping the source cell onto the given targets would do,
 * without changing anything. The caller shows the numbers, asks for
 * confirmation when something gets overwritten, and only then applies.
 */
export function shelvingBulkPlan(
  module: WardrobeModule,
  sourceCellId: string,
  targetIds: readonly string[]
): ShelvingBulkPlan {
  if (module.type !== "shelving") return EMPTY_PLAN;
  const grid = getShelvingGridLayout(module);
  const sections = getShelvingDiagonalSections(module, grid);
  // Ячейки под диагональю и внутри объединения отдельно не заполняются.
  const layouts = new Map(getShelvingCellLayouts(module, grid).map((layout) => [layout.id, layout]));
  const covered = new Set([
    ...sections.flatMap((section) => section.coveredCellIds),
    ...Array.from({ length: grid.columns * grid.rows }, (_, index) =>
      shelvingCellId(module.id, index % grid.columns, Math.floor(index / grid.columns)))
      .filter((id) => !layouts.has(id))
  ]);
  const cells = module.shelvingCells ?? [];
  const sourcePosition = parseShelvingCellId(sourceCellId);
  if (!sourcePosition || sourcePosition.moduleId !== module.id) return EMPTY_PLAN;
  // Cells are stored sparsely: an untouched opening is a valid source too.
  const source =
    cells.find((cell) => cell.id === sourceCellId) ??
    defaultShelvingCell(module.id, sourcePosition.column, sourcePosition.row);
  if (source.content === "diagonal" || covered.has(sourceCellId)) {
    return EMPTY_PLAN;
  }
  const filling = shelvingCellFilling(source);

  const plan: ShelvingBulkPlan = {
    apply: [],
    skipped: [],
    adjusted: [],
    overwritten: []
  };

  for (const id of new Set(targetIds)) {
    if (id === sourceCellId) continue;
    const parsed = parseShelvingCellId(id);
    if (!parsed || parsed.moduleId !== module.id) continue;
    const { column, row } = parsed;
    if (column >= grid.columns || row >= grid.rows) continue;
    const existing = cells.find((cell) => cell.id === id);
    if (covered.has(id) || existing?.content === "diagonal") {
      plan.skipped.push({ id, column, row, reason: "diagonal" });
      continue;
    }
    const width = layouts.get(id)?.width ?? grid.cellWidths[column] ?? 0;
    const height = layouts.get(id)?.height ?? grid.cellHeights[row] ?? 0;
    if (!shelvingCellFits(filling.content, width, height)) {
      plan.skipped.push({ id, column, row, reason: "size" });
      continue;
    }
    const patch: ShelvingCellFilling = { ...filling };
    if (patch.extraDivider && shelvingExtraDividerPosition(width, patch.extraDividerPosition) === null) {
      patch.extraDivider = false;
      patch.extraDividerMode = undefined;
      patch.extraDividerPosition = undefined;
      patch.extraDividerAngle = undefined;
      plan.adjusted.push(id);
    }
    if (existing && shelvingCellIsConfigured(existing)) plan.overwritten.push(id);
    plan.apply.push({ id, column, row, patch });
  }
  return plan;
}

/**
 * Builds the cell list the module should end up with. Kept pure so the store
 * action stays a single assignment and the behaviour stays testable without React.
 */
export function applyShelvingBulkPlan(
  module: WardrobeModule,
  plan: ShelvingBulkPlan
): ShelvingCellConfig[] {
  const cells = [...(module.shelvingCells ?? [])];
  for (const entry of plan.apply) {
    const index = cells.findIndex((cell) => cell.id === entry.id);
    const base =
      index >= 0
        ? cells[index]!
        : defaultShelvingCell(module.id, entry.column, entry.row);
    const next: ShelvingCellConfig = {
      ...base,
      ...entry.patch,
      id: entry.id,
      column: entry.column,
      row: entry.row
    };
    if (index >= 0) cells[index] = next;
    else cells.push(next);
  }
  return cells;
}
